import styled from "styled-components";
import { useState, useEffect } from "react";
import db from "../../config/firebase.config";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { BiUpvote, BiDownvote } from "react-icons/bi";
import { GoArrowUp, GoArrowDown } from "react-icons/go";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { MyComment } from "./MyComment";
import { UserNameWithBadge } from "../common/UserNameWithBadge";

dayjs.extend(relativeTime);

const FALLBACK_AVATAR =
    "https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png";

export const AllComments = ({ questionId, questionUserId }) => {
    const [comments, setComments] = useState([]);

    useEffect(() => {
        if (!questionId) return;
        const unsub = db
            .collection(`questions/${questionId}/comments`)
            .onSnapshot((snap) => {
                const data = [];
                snap.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));
                setComments(data);
            });
        return () => unsub();
    }, [questionId]);

    // Build a threaded tree from flat comments using parent_id
    const byId = {};
    comments.forEach((c) => (byId[c.id] = { ...c, children: [] }));

    const sortFn = (a, b) => (a.created_at || 0) - (b.created_at || 0);

    const roots = [];
    comments.forEach((c) => {
        const node = byId[c.id];
        if (c.parent_id && byId[c.parent_id]) {
            byId[c.parent_id].children.push(node);
        } else {
            roots.push(node);
        }
    });
    roots.sort(sortFn);
    comments.forEach((c) => byId[c.id].children.sort(sortFn));

    if (!comments.length)
        return (
            <Empty>💬 No comments yet. Be the first to comment!</Empty>
        );

    return (
        <CommentsWrap>
            {roots.map((node) => (
                <CommentThread
                    key={node.id}
                    node={node}
                    questionId={questionId}
                    questionUserId={questionUserId}
                />
            ))}
        </CommentsWrap>
    );
};

const CommentThread = ({ node, questionId, questionUserId, depth = 0 }) => {
    const user = useSelector(selectUser);
    const [replying, setReplying] = useState(false);
    const [collapsed, setCollapsed] = useState(true);

    const c = node;
    const isOP = questionUserId && c.user_id === questionUserId;
    const upCount = (c.upvoters || []).length;
    const isUp = user?.uid && (c.upvoters || []).includes(user.uid);
    const isDown = user?.uid && (c.downvoters || []).includes(user.uid);
    const replies = node.children || [];
    const replyCount = replies.length;

    const toggleVote = async (type) => {
        if (!user || !questionId) return;
        const ref = db
            .collection(`questions/${questionId}/comments`)
            .doc(c.id);
        const doc = await ref.get();
        if (!doc.exists) return;
        const data = doc.data();
        const upvoters = data.upvoters || [];
        const downvoters = data.downvoters || [];

        let nextUp = upvoters;
        let nextDown = downvoters;

        if (type === "up") {
            if (upvoters.includes(user.uid)) {
                nextUp = upvoters.filter((u) => u !== user.uid);
            } else {
                nextUp = [...upvoters, user.uid];
                nextDown = downvoters.filter((u) => u !== user.uid);
            }
        } else {
            if (downvoters.includes(user.uid)) {
                nextDown = downvoters.filter((u) => u !== user.uid);
            } else {
                nextDown = [...downvoters, user.uid];
                nextUp = upvoters.filter((u) => u !== user.uid);
            }
        }

        await ref.update({ upvoters: nextUp, downvoters: nextDown });
    };

    return (
        <Thread depth={Math.min(depth, 5)}>
            <div className="comment">
                <div className="head">
                    <img
                        src={c.user_img || FALLBACK_AVATAR}
                        alt={c.user_name || "commenter"}
                    />
                    <div className="meta">
                        <UserNameWithBadge
                            name={c.user_name || "Anonymous"}
                            uid={c.user_id}
                            email={c.user_email}
                            badgeSize="sm"
                            className="name"
                        />
                        {isOP && <span className="op-badge">OP</span>}
                        <span className="time">
                            •{" "}
                            {c.created_at
                                ? dayjs(c.created_at).fromNow()
                                : "just now"}
                        </span>
                    </div>
                </div>

                <p className="text">{c.comment}</p>

                <div className="actions">
                    <button
                        className={`vote ${isUp ? "voted-up" : ""}`}
                        onClick={() => toggleVote("up")}
                    >
                        {isUp ? <GoArrowUp /> : <BiUpvote />}
                        <span>{upCount || 0}</span>
                    </button>
                    <button
                        className={`vote ${isDown ? "voted-down" : ""}`}
                        onClick={() => toggleVote("down")}
                    >
                        {isDown ? <GoArrowDown /> : <BiDownvote />}
                    </button>
                    <button
                        className={`act ${replying ? "active" : ""}`}
                        onClick={() => setReplying(!replying)}
                    >
                        Reply
                    </button>
                    <button className="act">Award</button>
                    <button className="act">Share</button>
                </div>

                {replying && (
                    <div className="reply-box">
                        <MyComment
                            questionId={questionId}
                            parentId={c.id}
                            replyTo={c.user_name}
                            autoFocus
                            onSubmit={() => setReplying(false)}
                        />
                    </div>
                )}
            </div>

            {replyCount > 0 && (
                <button
                    className="more-replies"
                    onClick={() => setCollapsed(!collapsed)}
                >
                    {collapsed
                        ? `${replyCount} ${
                              replyCount === 1 ? "more reply" : "more replies"
                          }`
                        : "Hide replies"}
                </button>
            )}

            {!collapsed && (
                <div className="children">
                    {replies.map((child) => (
                        <CommentThread
                            key={child.id}
                            node={child}
                            questionId={questionId}
                            questionUserId={questionUserId}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </Thread>
    );
};

const Empty = styled.div`
    padding: 12px 16px;
    font-size: 18px;
    color: #999;
    font-style: italic;
`;

const CommentsWrap = styled.div`
    padding: 8px 12px 12px;
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border-top: 1px solid rgba(201, 160, 99, 0.15);
`;

const Thread = styled.div`
    .comment {
        padding: 8px 0 2px;
    }

    .head {
        display: flex;
        align-items: center;
        gap: 8px;

        img {
            width: 34px;
            height: 34px;
            border-radius: 50%;
            object-fit: cover;
            flex-shrink: 0;
            border: 1.5px solid rgba(201, 160, 99, 0.3);
        }

        .meta {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;

            .name {
                font-size: 20px;
                font-weight: 700;
                color: #0a1428;
            }

            .op-badge {
                font-size: 14px;
                font-weight: 800;
                color: #1DA1F2;
                background: #e8f5fe;
                border: 1px solid #1DA1F2;
                border-radius: 3px;
                padding: 0 6px;
                line-height: 1.4;
            }

            .time {
                font-size: 16px;
                color: #878a8c;
            }
        }
    }

    .text {
        font-size: 20px;
        color: #1a1a1b;
        line-height: 1.4;
        margin: 4px 0 4px 42px;
        word-break: break-word;
    }

    .actions {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-left: 38px;

        button {
            display: flex;
            align-items: center;
            gap: 4px;
            background: transparent;
            border: none;
            cursor: pointer;
            color: #878a8c;
            font-size: 17px;
            font-weight: 700;
            padding: 4px 10px;
            border-radius: 14px;
            transition: background 0.15s, color 0.15s;

            svg {
                font-size: 1.25rem;
            }

            &:hover {
                background: #e8f5fe;
                color: #1DA1F2;
            }

            &.voted-up {
                color: #1DA1F2;
                background: #e8f5fe;
            }

            &.voted-down {
                color: #e53935;
                background: #ffebee;
            }

            &.act.active {
                color: #1DA1F2;
                background: #e8f5fe;
            }
        }
    }

    .reply-box {
        margin: 6px 0 6px 38px;
    }

    .more-replies {
        margin: 4px 0 4px 38px;
        background: transparent;
        border: none;
        color: #1DA1F2;
        font-size: 17px;
        font-weight: 700;
        cursor: pointer;
        padding: 3px 8px;
        border-radius: 6px;

        &:hover {
            background: #e8f5fe;
        }
    }

    .children {
        margin-left: 16px;
        padding-left: 12px;
        border-left: 2px solid #e7e9ee;
    }
`;
